import { visit } from 'unist-util-visit';
import type { Plugin, Transformer } from 'unified';
import type { Root, Text } from 'mdast';

/**
 * WikiLink format:
 * - [[Page Title]] -> link to /pages/page-title with text "Page Title"
 * - [[Page Title|Display Text]] -> link to /pages/page-title with text "Display Text"
 * - [[page-slug]] -> link to /pages/page-slug with text "page-slug"
 */

export interface WikiLinkOptions {
  /**
   * Base path for wiki links
   * @default '/pages'
   */
  basePath?: string;

  /**
   * Class name for wiki links
   * @default 'wiki-link'
   */
  className?: string;

  /**
   * Class name for broken links (page doesn't exist)
   * @default 'wiki-link-broken'
   */
  brokenClassName?: string;
}

interface WikiLinkNode {
  type: 'link';
  url: string;
  title: null;
  children: Array<{ type: 'text'; value: string }>;
  data?: {
    hProperties?: {
      className?: string;
    };
  };
}

/**
 * Convert a title or text to a slug
 */
function titleToSlug(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w\u4e00-\u9fa5-]/g, '');
}

/**
 * Parse WikiLink syntax from text
 */
function parseWikiLinks(
  text: string,
  options: Required<WikiLinkOptions>
): Array<Text | WikiLinkNode> {
  const nodes: Array<Text | WikiLinkNode> = [];
  const regex = /\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      nodes.push({
        type: 'text',
        value: text.slice(lastIndex, match.index),
      });
    }

    const pageTitle = match[1].trim();
    const displayText = match[2]?.trim() || pageTitle;
    const slug = titleToSlug(pageTitle);

    nodes.push({
      type: 'link',
      url: `${options.basePath}/${slug}`,
      title: null,
      children: [{ type: 'text', value: displayText }],
      data: {
        hProperties: {
          className: options.className,
        },
      },
    });

    lastIndex = regex.lastIndex;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    nodes.push({
      type: 'text',
      value: text.slice(lastIndex),
    });
  }

  return nodes.length > 0 ? nodes : [{ type: 'text', value: text }];
}

/**
 * Remark plugin to transform WikiLinks to links
 */
export const remarkWikiLink: Plugin<[WikiLinkOptions?], Root> = (
  options: WikiLinkOptions = {}
): Transformer<Root> => {
  const settings: Required<WikiLinkOptions> = {
    basePath: options.basePath || '/pages',
    className: options.className || 'wiki-link',
    brokenClassName: options.brokenClassName || 'wiki-link-broken',
  };

  return (tree: Root) => {
    visit(tree, 'text', (node: Text, index, parent) => {
      if (!node.value.includes('[[') || index === undefined || !parent) {
        return;
      }

      const newNodes = parseWikiLinks(node.value, settings);

      if (newNodes.length > 1 || newNodes[0].type !== 'text') {
        // Replace the text node with new nodes
        parent.children.splice(index, 1, ...newNodes);
      }
    });
  };
};

export default remarkWikiLink;
